import React, { useEffect, useState, useMemo } from 'react';
import {
  Container, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Box, Chip, Alert, Paper
} from '@mui/material';
import AdminTable from '../../components/shared/AdminTable';
import billingApi from '../../services/billingApi';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import useConfirm from '../../hooks/useConfirm';
import AdminPageSkeleton from '../../components/shared/AdminPageSkeleton';

export default function ProductManagement() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [form, setForm] = useState({ productId: '', name: '', description: '', webhook_url: '', api_url: '', catalog_key: '' });
  const [editProduct, setEditProduct] = useState(null);

  const { confirm, confirmState, closeConfirm } = useConfirm();

  useEffect(() => { loadProducts(); }, []);

  async function loadProducts() {
    try {
      const data = await billingApi.get('/api/admin/products');
      setProducts(data.products);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    try {
      const data = await billingApi.post('/api/admin/products', form);
      setNewApiKey(data.api_key);
      setDialog(false);
      setForm({ productId: '', name: '', description: '', webhook_url: '' });
      loadProducts();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleRotateKey(id) {
    confirm({
      title: 'Rotate API Key',
      message: 'Rotate API key? The old key will stop working immediately.',
      destructive: true,
      confirmLabel: 'Rotate Key',
      onConfirm: async () => {
        try {
          const data = await billingApi.post(`/api/admin/products/${id}/rotate-key`);
          setNewApiKey(data.api_key);
        } catch (err) {
          setError(err.message);
        }
      },
    });
  }

  function handleEdit(product) {
    setEditProduct(product);
  }

  async function handleSaveEdit() {
    try {
      await billingApi.put(`/api/admin/products/${editProduct.id}`, {
        api_url: editProduct.api_url,
        catalog_key: editProduct.catalog_key,
        webhook_url: editProduct.webhook_url
      });
      setEditProduct(null);
      loadProducts();
    } catch (err) {
      setError(err.message);
    }
  }

  const columns = useMemo(() => [
    {
      field: 'productId',
      headerName: 'ID',
      width: 150,
      renderCell: (row) => <code>{row.productId}</code>,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      type: 'singleSelect',
      filterOptions: ['Active', 'Inactive'],
      valueGetter: ({ row }) => row.is_active ? 'Active' : 'Inactive',
      renderCell: (row) => {
        const label = row.is_active ? 'Active' : 'Inactive';
        return <Chip label={label} color={label === 'Active' ? 'success' : 'default'} size="small" />;
      },
    },
    {
      field: 'api_url',
      headerName: 'API URL',
      width: 250,
      renderCell: (row) => (
        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {row.api_url || '—'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (row) => (
        <>
          <Button size="small" onClick={() => handleEdit(row)}>Edit</Button>
          <Button size="small" onClick={() => handleRotateKey(row.id)}>Rotate Key</Button>
        </>
      ),
    },
  ], []);

  if (loading) return <AdminPageSkeleton />;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Products</Typography>
        <Button variant="contained" onClick={() => setDialog(true)}>Add Product</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {newApiKey && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setNewApiKey('')}>
          <strong>Save this API key now!</strong> It cannot be retrieved again.
          <Box sx={{ mt: 1, fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", wordBreak: 'break-all' }}>{newApiKey}</Box>
        </Alert>
      )}

      <Paper variant="outlined" sx={{ height: 400 }}>
        <AdminTable
          rows={products}
          columns={columns}
          defaultSort={[]}
          defaultPageSize={25}
        />
      </Paper>

      {/* Add Product Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)}>
        <DialogTitle>Add Product</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Product ID" value={form.productId} onChange={e => setForm({ ...form, productId: e.target.value })} sx={{ mt: 1, mb: 2 }} />
          <TextField fullWidth label="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} sx={{ mb: 2 }} />
          <TextField fullWidth label="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} sx={{ mb: 2 }} />
          <TextField fullWidth label="API URL" value={form.api_url} onChange={e => setForm({ ...form, api_url: e.target.value })} sx={{ mb: 2 }} placeholder="http://localhost:5001" />
          <TextField fullWidth label="Catalog Key" value={form.catalog_key} onChange={e => setForm({ ...form, catalog_key: e.target.value })} sx={{ mb: 2 }} placeholder="Shared secret for feature catalog" />
          <TextField fullWidth label="Webhook URL" value={form.webhook_url} onChange={e => setForm({ ...form, webhook_url: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!form.productId || !form.name}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Product Dialog */}
      {editProduct && (
        <Dialog open={!!editProduct} onClose={() => setEditProduct(null)} maxWidth="md" fullWidth>
          <DialogTitle>Edit {editProduct.name}</DialogTitle>
          <DialogContent>
            <TextField fullWidth label="API URL" value={editProduct.api_url || ''} onChange={e => setEditProduct({ ...editProduct, api_url: e.target.value })} sx={{ mt: 1, mb: 2 }} placeholder="http://localhost:5001" helperText="Base URL of the product's backend API" />
            <TextField fullWidth label="Catalog Key" value={editProduct.catalog_key || ''} onChange={e => setEditProduct({ ...editProduct, catalog_key: e.target.value })} sx={{ mb: 2 }} placeholder="Shared secret for feature catalog" helperText="Used by Refresh Catalog on the Pricing page" />
            <TextField fullWidth label="Webhook URL" value={editProduct.webhook_url || ''} onChange={e => setEditProduct({ ...editProduct, webhook_url: e.target.value })} helperText="Notified on subscription changes" />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditProduct(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveEdit}>Save</Button>
          </DialogActions>
        </Dialog>
      )}

      <ConfirmDialog {...confirmState} onClose={closeConfirm} />
    </Container>
  );
}
